import {
  createContext, useContext, useEffect, useState,
  useCallback, useRef, type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { usePlanetContext } from './PlanetContext'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AttackFleet {
  id: string
  attacker: string
  mission: 'attack' | 'group_attack'
  arrives_at: string
  planet_name: string
  target_galaxy: number
  target_system: number
  target_pos: number
}

export interface Transient {
  id: string
  type: 'build_done' | 'research_done' | 'fleet_returned'
  label: string
  ts: number
}

interface NotifValue {
  attacks: AttackFleet[]
  unread: number
  chatUnread: number
  transients: Transient[]
  dismiss: (id: string) => void
  resetUnread: () => void
  resetChatUnread: () => void
  count: number
  urgent: boolean
}

const Ctx = createContext<NotifValue>({
  attacks: [], unread: 0, chatUnread: 0, transients: [],
  dismiss: () => {}, resetUnread: () => {}, resetChatUnread: () => {},
  count: 0, urgent: false,
})

// ── Provider ──────────────────────────────────────────────────────────────────

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { planets } = usePlanetContext()
  const [userId,     setUserId]     = useState<string | null>(null)
  const [attacks,    setAttacks]    = useState<AttackFleet[]>([])
  const [unread,     setUnread]     = useState(0)
  const [chatUnread, setChatUnread] = useState(0)
  const [transients, setTransients] = useState<Transient[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  // ── Transient helpers ──────────────────────────────────────────────────────

  function addTransient(t: Omit<Transient, 'ts'>) {
    const notif: Transient = { ...t, ts: Date.now() }
    setTransients(prev => [notif, ...prev.slice(0, 9)])
    const timer = setTimeout(() => {
      setTransients(prev => prev.filter(x => x.id !== notif.id))
      timers.current.delete(notif.id)
    }, 10_000)
    timers.current.set(notif.id, timer)
  }

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id)
    if (t) { clearTimeout(t); timers.current.delete(id) }
    setTransients(prev => prev.filter(x => x.id !== id))
  }, [])

  // ── Auth ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null))
  }, [])

  // ── Reset helpers (called from GameLayout on route change) ─────────────────

  const resetUnread = useCallback(() => {
    if (!userId) return
    supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .eq('is_read', false)
      .then(({ count }) => setUnread(count ?? 0))
  }, [userId])

  const resetChatUnread = useCallback(async () => {
    if (!userId) return
    const { data: convs } = await supabase
      .from('conversations')
      .select('id')
      .or(`participant_a.eq.${userId},participant_b.eq.${userId}`)
    const ids = (convs ?? []).map((c: any) => c.id)
    if (ids.length === 0) { setChatUnread(0); return }
    const { count } = await supabase
      .from('chat_messages')
      .select('id', { count: 'exact', head: true })
      .in('conversation_id', ids)
      .neq('sender_id', userId)
      .eq('is_read', false)
    setChatUnread(count ?? 0)
  }, [userId])

  // ── Initial data load ──────────────────────────────────────────────────────

  useEffect(() => {
    if (planets.length === 0) return
    const myCoords = new Set(planets.map(p => `${p.galaxy}:${p.system}:${p.position}`))
    const planetMap = new Map(planets.map(p => [`${p.galaxy}:${p.system}:${p.position}`, p]))

    supabase
      .from('fleet_movements')
      .select('id, user_id, mission, arrives_at, target_galaxy, target_system, target_pos, profiles(username)')
      .in('mission', ['attack', 'group_attack'])
      .eq('is_returning', false)
      .gt('arrives_at', new Date().toISOString())
      .then(({ data }) => {
        if (!data) return
        const incoming: AttackFleet[] = (data as any[])
          .filter(f => myCoords.has(`${f.target_galaxy}:${f.target_system}:${f.target_pos}`))
          .map(f => {
            const key    = `${f.target_galaxy}:${f.target_system}:${f.target_pos}`
            const planet = planetMap.get(key)
            return {
              id: f.id,
              attacker: (f.profiles as any)?.username ?? 'Unbekannt',
              mission: f.mission,
              arrives_at: f.arrives_at,
              planet_name: planet?.name ?? key,
              target_galaxy: f.target_galaxy,
              target_system: f.target_system,
              target_pos: f.target_pos,
            }
          })
        setAttacks(incoming)
      })
  }, [planets])

  useEffect(() => {
    if (!userId) return
    supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .eq('is_read', false)
      .then(({ count }) => setUnread(count ?? 0))
  }, [userId])

  useEffect(() => {
    if (!userId) return
    async function load() {
      const { data: convs } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant_a.eq.${userId},participant_b.eq.${userId}`)
      const ids = (convs ?? []).map((c: any) => c.id)
      if (ids.length === 0) { setChatUnread(0); return }
      const { count } = await supabase
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', ids)
        .neq('sender_id', userId)
        .eq('is_read', false)
      setChatUnread(count ?? 0)
    }
    load()
  }, [userId])

  // ── Realtime subscriptions ─────────────────────────────────────────────────

  useEffect(() => {
    if (!userId || planets.length === 0) return

    const myCoords  = new Set(planets.map(p => `${p.galaxy}:${p.system}:${p.position}`))
    const planetMap = new Map(planets.map(p => [`${p.galaxy}:${p.system}:${p.position}`, p]))

    const channel = supabase
      .channel('notifications-main')

      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'fleet_movements',
      }, (payload) => {
        const f = payload.new as any
        if (!['attack', 'group_attack'].includes(f.mission)) return
        if (f.is_returning) return
        const key = `${f.target_galaxy}:${f.target_system}:${f.target_pos}`
        if (!myCoords.has(key)) return
        supabase.from('profiles').select('username').eq('id', f.user_id).maybeSingle()
          .then(({ data: p }) => {
            const planet = planetMap.get(key)
            setAttacks(prev => [...prev.filter(a => a.id !== f.id), {
              id: f.id,
              attacker: p?.username ?? 'Unbekannt',
              mission: f.mission,
              arrives_at: f.arrives_at,
              planet_name: planet?.name ?? key,
              target_galaxy: f.target_galaxy,
              target_system: f.target_system,
              target_pos: f.target_pos,
            }])
          })
      })

      .on('postgres_changes', {
        event: 'DELETE', schema: 'public', table: 'fleet_movements',
      }, (payload) => {
        const f = payload.old as any
        if (['attack', 'group_attack'].includes(f.mission) && !f.is_returning) {
          setAttacks(prev => prev.filter(a => a.id !== f.id))
        }
        if (f.user_id === userId && f.is_returning) {
          const parts: string[] = []
          if (f.cargo_metal   > 0) parts.push(`${Math.round(f.cargo_metal)} M`)
          if (f.cargo_crystal > 0) parts.push(`${Math.round(f.cargo_crystal)} K`)
          if (f.cargo_deut    > 0) parts.push(`${Math.round(f.cargo_deut)} D`)
          addTransient({
            id:    `fleet-${f.id}-${Date.now()}`,
            type:  'fleet_returned',
            label: parts.length > 0 ? `Flotte zurück +${parts.join(' / ')}` : 'Flotte zurückgekehrt',
          })
        }
      })

      .on('postgres_changes', {
        event: 'DELETE', schema: 'public', table: 'build_queue',
      }, (payload) => {
        const item = payload.old as any
        const name = (item.item_type as string).replace(/_/g, ' ')
        const cat  = item.type === 'ship' ? 'Werft' : item.type === 'defense' ? 'Abwehr' : 'Infrastruktur'
        addTransient({
          id:    `build-${item.id}-${Date.now()}`,
          type:  'build_done',
          label: `${name} fertig (${cat})`,
        })
      })

      .on('postgres_changes', {
        event: 'DELETE', schema: 'public', table: 'research_queue',
      }, (payload) => {
        const item = payload.old as any
        const name = (item.research_type as string).replace(/_/g, ' ')
        addTransient({
          id:    `research-${item.id}-${Date.now()}`,
          type:  'research_done',
          label: `${name} erforscht`,
        })
      })

      // INSERT: increment immediately
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `receiver_id=eq.${userId}`,
      }, () => {
        setUnread(prev => prev + 1)
      })

      // UPDATE: re-query (no column filter — needs REPLICA IDENTITY FULL otherwise)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'messages',
      }, () => {
        supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('receiver_id', userId)
          .eq('is_read', false)
          .then(({ count }) => setUnread(count ?? 0))
      })

      // INSERT chat: increment if not own message
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messages',
      }, (payload) => {
        const m = payload.new as any
        if (m.sender_id !== userId) setChatUnread(prev => prev + 1)
      })

      // UPDATE chat: re-query
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'chat_messages',
      }, async () => {
        const { data: convs } = await supabase
          .from('conversations')
          .select('id')
          .or(`participant_a.eq.${userId},participant_b.eq.${userId}`)
        const ids = (convs ?? []).map((c: any) => c.id)
        if (ids.length === 0) { setChatUnread(0); return }
        const { count } = await supabase
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .in('conversation_id', ids)
          .neq('sender_id', userId)
          .eq('is_read', false)
        setChatUnread(count ?? 0)
      })

      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [userId, planets])

  const count  = attacks.length + (unread > 0 ? 1 : 0) + (chatUnread > 0 ? 1 : 0) + transients.length
  const urgent = attacks.length > 0

  return (
    <Ctx.Provider value={{
      attacks, unread, chatUnread, transients,
      dismiss, resetUnread, resetChatUnread,
      count, urgent,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export function useNotifications() {
  return useContext(Ctx)
}
